import { z } from "zod";
import { addressSchema } from "@/types/schemas/address";
import { CommodityEnumSchema, DistributionAreaEnumSchema } from "@/types/contract";
import { dateSchema } from "@/types/schemas/date";
import { specificationSchema } from "./specifications";

export const applianceSchema = z.object({
  id: z.nullable(z.number().or(z.string())),
  count: z.nullable(z.number().or(z.string())),
  consumption: z.nullable(z.number()),
  powerApplianceId: z.number().nullable().default(null),
});

export const gasApplianceSchema = z.object({
  id: z.nullable(z.number().or(z.string())),
  count: z.nullable(z.number().or(z.string())),
  consumption: z.nullable(z.number()),
  gasApplianceId: z.number().nullable().default(null),
});

export const heatPumpSchema = z.object({
  frequencyConverter: z.boolean(),
  startingCurrent: z.number().nullable(),
  steadyStateCurrent: z.number().nullable(),
  ratedPower: z.number().nullable(),
  ratedVoltage: z.number().nullable(),
  id: z.nullable(z.number().or(z.string())),
});

export const sopSchema = z.nullable(
  z.object({
    self: z.boolean().nullable(),
    supplyPointUsage: z.nullable(z.string()),
    tdS01: z.boolean().nullable(),
    tdS02: z.boolean().nullable(),
    tdS03: z.boolean().nullable(),
    tdS04: z.boolean().nullable(),
    gasMeterStatus: z.nullable(z.string()),
    gasAppliance: z.array(gasApplianceSchema).default([]),
    powerAppliance: z.array(applianceSchema).default([]),
    heatPump: z.array(heatPumpSchema).default([]),
    statusId: z.number().nullable(),
    revisionReport: z.string().nullable(),
    specifications: z.array(specificationSchema).optional(),
    statusTechnicalId: z.number().nullable().default(null),
    statusConstructionId: z.number().nullable().default(null),
    statusFinancialId: z.number().nullable().default(null),
    id: z.number(),
  })
);

export const previousContractSchema = z.object({
  id: z.number(),
  type: z.nullable(z.string()),
  validTo: dateSchema,
  noticePeriodValue: z.nullable(z.number()),
  noticePeriodUnit: z.nullable(z.string()),
  previousSupplierId: z.nullable(z.string()),
  previousSupplierName: z.nullable(z.string()),
  previousSupplierProductId: z.nullable(z.string()),
  previousSupplierProductName: z.nullable(z.string()),
  previousCustomerName: z.nullable(z.string()),
  previousCustomerIdentification: z.nullable(z.string()),
  transferByEon: z.boolean(),
  determinationReason: z.nullable(z.string()),
  terminationType: z.nullable(z.string()),
  processReason: z.nullable(z.string()),
  changeOfCustomerReason: z.nullable(z.string()),
});

export const supplyPointSchema = z.object({
  commodity: CommodityEnumSchema,
  code: z.nullable(z.string()),
  tariff: z.nullable(z.string()),
  tariffId: z.nullable(z.string().or(z.number())),
  circuitBreakerPhase: z.nullable(z.number()),
  circuitBreakerValue: z.nullable(z.number()),
  connectionType: z.nullable(z.string()),
  yearConsumption: z.nullable(z.number()),
  yearConsumptionUnits: z
    .nullable(z.string())
    .default(null)
    .transform(value => (value === "" ? null : value)),
  yearConsumptionNt: z.number().nullable().default(null),
  yearConsumptionNtUnits: z.string().nullable().default(null),
  yearConsumptionVt: z.number().nullable().default(null),
  yearConsumptionVtUnits: z.string().nullable().default(null),
  measurementType: z.nullable(z.string()),
  supplyPointType: z.string(),
  depositFrequency: z.nullable(z.string()),
  overpaidFrequencyId: z.nullable(z.string().or(z.number())).default(null),
  depositAmount: z.nullable(z.string().or(z.number())),
  depositRecommendation: z.nullable(z.string()),
  previousContract: z.nullable(previousContractSchema),
  previousConnection: z.nullable(z.boolean()).default(true), // TODO change to null
  address: z.nullable(
    z
      .object({
        type: z.string().nullable(),
      })
      .merge(addressSchema)
  ),
  sop: sopSchema,
  sopNumber: z.string().nullable(),
  situationPlan: z.enum(["attachment", "drawing", "finished_drawing"]).nullable().optional(),
  district: z.string().nullable().default(null),
  distribution: z.object({
    distributionGridId: z.nullable(z.string()),
    gridLevelType: z.nullable(z.string()),
    gridLevel: z.nullable(z.string()),
    distributorId: z.nullable(z.string().or(z.number())),
  }),
  distributionArea: z.nullable(DistributionAreaEnumSchema).default(null),
  services: z.array(z.unknown()),
  floor: z.nullable(z.string().or(z.number())),
  region: z.string().nullable().default(null),
  room: z.nullable(z.string().or(z.number())),
  propertyRelation: z.nullable(z.string()),
  propertyOwnerName: z.nullable(z.string()),
  temporalityId: z.nullable(z.string().or(z.number())),
  ldsOrMeasurementTypeB: z.boolean(),
  tariffChangeEntrepreneurDForBusinessId: z.nullable(z.string().or(z.number())),
  tariffChangeEntrepreneurDForBusinessDoubleMeasurementId: z.nullable(z.string().or(z.number())),
  tariffChangeEntrepreneurForBusinessDoubleMeasurementId: z.nullable(z.string().or(z.number())),
  tariffChangeEntrepreneurForBusinessId: z.nullable(z.string().or(z.number())),
  tariffChangeEntrepreneurForLivingId: z.nullable(z.string().or(z.number())),
  tariffChangeHouseholdId: z.nullable(z.string().or(z.number())),
  tariffForNewContractId: z.nullable(z.string().or(z.number())),
  codeSecondary: z.nullable(z.string()),
  exemptionGasTax: z.boolean(),
  forLiving: z.boolean().nullable(),
  license: z.boolean().nullable(),
  licenseNumber: z.string().nullable(),
  installedPower: z.number().nullable(),
  actualPower: z.number().nullable(),
  reservedPower: z.number().nullable(),
  battery: z.boolean().nullable(),
  batteryCapacity: z.number().nullable(),
  trafficDate: dateSchema,
  customerName: z.string().nullable(),
  forbiddenOverflow: z.boolean().nullable().default(false),
  note: z.string().nullable().default(null),
  kettleBrand: z.string().nullable().default(null),
  depositMethod: z.number().nullable().default(null),
  measurementCategory: z.number().nullable().default(null),
  changeTariff: z.boolean().nullable().default(null),
  changeCircuitBreaker: z.boolean().nullable().default(null),
  currentTariff: z.string().nullable().default(null),
  currentTariffId: z.string().or(z.number()).nullable().default(null),
  currentCircuitBreakerPhase: z.number().nullable().default(null),
  currentCircuitBreakerValue: z.number().nullable().default(null),
  currentDepositAmount: z.string().or(z.number()).nullable().default(null),
  currentDepositFrequency: z.string().nullable().default(null),
  doubleMeasurement: z.boolean().nullable().default(null),
  totalDismantling: z.boolean().nullable().default(false),
  electricityProduction: z.boolean().nullable().default(null),
  used: z.boolean().nullable().default(false),
  curtailmentLevelExemption: z.boolean().default(false),
  curtailmentLevel4: z.number().nullable().default(null),
  curtailmentLevel6: z.number().nullable().default(null),
  curtailmentLevel7Minimum: z.number().nullable().default(null),
  curtailmentLevel7Time: z.number().nullable().default(null),
  mtp1Number: z.string().nullable().default(null),
  mtp1Ratio: z.string().nullable().default(null),
  mtp1Class: z.string().nullable().default(null),
  mtp2Number: z.string().nullable().default(null),
  mtp2Ratio: z.string().nullable().default(null),
  mtp2Class: z.string().nullable().default(null),
  mtp3Number: z.string().nullable().default(null),
  mtp3Ratio: z.string().nullable().default(null),
  mtp3Class: z.string().nullable().default(null),
});
